import { useMutation } from '@apollo/client/react';
import md5 from 'md5';

import { AppLogoDirectUploadDocument } from 'src/graphql/actions/apps/appLogoDirectUpload.mutation';
import { ClientError } from 'src/utils/errors';

const computeChecksum = async (file: File) => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const digestBytes = md5(bytes, { asBytes: true });
  const binary = digestBytes.map((byte) => String.fromCharCode(byte)).join('');

  return btoa(binary);
};

export const useAppLogoUpload = () => {
  const [appLogoDirectUpload] = useMutation(AppLogoDirectUploadDocument);

  const uploadAppLogo = async (file: File) => {
    const checksum = await computeChecksum(file);

    const { data } = await appLogoDirectUpload({
      variables: {
        input: {
          filename: file.name,
          contentType: file.type,
          byteSize: file.size,
          checksum,
        },
      },
    });

    const result = data?.appLogoDirectUpload;

    if (!result?.success || !result.signedId || !result.url) {
      throw new ClientError(result?.errors?.[0] ?? 'Failed to prepare logo upload.');
    }

    const response = await fetch(result.url, {
      method: 'PUT',
      body: file,
      headers: result.headers,
    });

    if (!response.ok) {
      throw new ClientError('Failed to upload logo.');
    }

    return result.signedId;
  };

  return uploadAppLogo;
};
